import { AnimatePresence, motion } from "framer-motion";
import { classNames } from "primereact/utils";
import { Icon, IconType } from "../../../icons/Icon";
import { DismissibleNote } from "../../common/DismissibleNote/DismissibleNote";
import { EmptyStateCard } from "../../common/EmptyStateCard/EmptyStateCard";
import { ReorderDialog } from "../../ReorderDialog/ReorderDialog";
import { RenderWhen } from "../../../infrastructure/components/RenderWhen";
import { IGoalsDataSource } from "../models/goalModels";
import { useGoalsOverview } from "../hooks/useGoalsOverview";
import { GoalRow } from "../components/GoalRow/GoalRow";
import { GoalTextDialog } from "../components/GoalTextDialog/GoalTextDialog";
import styles from "./GoalsOverview.module.scss";

interface IGoalsOverviewProps {
  dataSource: IGoalsDataSource;
  /**
   * Short explanation of the page, shown above the goals. The storageKey remembers that
   * the user closed it, so it never comes back.
   */
  intro?: { storageKey: string; title: string; text: string };
}

// A done group sliding below the open ones (and back up when reopened) should read as a
// move, not a jump: soft and a little slower than a hover.
const MOVE = { layout: { duration: 0.4, ease: "easeInOut" }, opacity: { duration: 0.15 } };

/**
 * The goals ("Цели") shared by the user's own "Моите цели" and the staff page on a user's
 * profile: a two-level tree of goals and sub-goals that are checked off. Staff also add,
 * edit, delete and reorder; the client only checks and unchecks.
 */
export const GoalsOverview = ({ dataSource, intro }: IGoalsOverviewProps) => {
  const {
    goals,
    isLoading,
    hasAny,
    canEdit,
    toggleCompleted,
    dialogMode,
    openAdd,
    openEdit,
    closeDialog,
    requestDelete,
    reorderTarget,
    openReorder,
    closeReorder,
    saveOrder,
    isReordering,
  } = useGoalsOverview(dataSource);

  const rowHandlers = {
    canEdit,
    onToggle: toggleCompleted,
    onEdit: openEdit,
    onDelete: requestDelete,
  };

  return (
    <div className={styles.root}>
      <div className={styles.root_content}>
        {/* Only over a list: above the empty state alone the heading hangs in the void. */}
        <RenderWhen condition={!isLoading && hasAny}>
          <div className={styles.root_toolbar}>
            <h2 className={styles.root_toolbar_title}>Цели</h2>
            <RenderWhen condition={canEdit}>
              <div className={styles.root_toolbar_actions}>
                <RenderWhen condition={goals.length > 1}>
                  <button
                    type="button"
                    className={styles.root_toolbar_button}
                    onClick={() => openReorder()}
                  >
                    <Icon
                      type={IconType.DragHandle}
                      width={16}
                      height={16}
                      color="rgba(255, 255, 255, 0.85)"
                    />
                    Подреди
                  </button>
                </RenderWhen>
                <button
                  type="button"
                  className={styles.root_toolbar_button}
                  onClick={() => openAdd()}
                >
                  + Добави цел
                </button>
              </div>
            </RenderWhen>
          </div>
        </RenderWhen>

        <RenderWhen condition={!!intro}>
          <DismissibleNote
            storageKey={intro?.storageKey ?? ""}
            title={intro?.title ?? ""}
            text={intro?.text ?? ""}
          />
        </RenderWhen>

        <RenderWhen condition={isLoading}>
          <div className={styles.root_tree}>
            {Array(3)
              .fill(0)
              .map((_, index) => (
                <div className={styles.root_skeleton} key={index} />
              ))}
          </div>
        </RenderWhen>

        <RenderWhen condition={!isLoading && !hasAny}>
          <EmptyStateCard
            icon={IconType.Trophy}
            title="Все още няма цели"
            description={
              canEdit
                ? "Добавете първата цел на клиента. Разделете я на подцели, за да се вижда напредъкът."
                : "Вашият треньор все още не е поставил цели."
            }
            actionLabel={canEdit ? "+ Добави цел" : undefined}
            onAction={canEdit ? () => openAdd() : undefined}
          />
        </RenderWhen>

        <RenderWhen condition={!isLoading && hasAny}>
          <div className={styles.root_tree}>
            <AnimatePresence initial={false}>
              {goals.map((goal) => (
                <motion.section
                  key={goal.id}
                  layout="position"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={MOVE}
                  className={classNames(styles.group, { [styles.group_done]: goal.isCompleted })}
                  data-testid="goal-group"
                >
                  <GoalRow
                    goal={goal}
                    {...rowHandlers}
                    onAddSubGoal={openAdd}
                    onReorderSubGoals={openReorder}
                  />

                  <RenderWhen condition={goal.subGoals.length > 0}>
                    <ul className={styles.group_subs}>
                      {goal.subGoals.map((subGoal) => (
                        <li key={subGoal.id} className={styles.group_sub}>
                          <GoalRow goal={subGoal} isSubGoal {...rowHandlers} />
                        </li>
                      ))}
                    </ul>
                  </RenderWhen>
                </motion.section>
              ))}
            </AnimatePresence>
          </div>
        </RenderWhen>
      </div>

      <RenderWhen condition={canEdit}>
        <GoalTextDialog mode={dialogMode} dataSource={dataSource} onHide={closeDialog} />

        <ReorderDialog
          visible={!!reorderTarget}
          onHide={closeReorder}
          title={reorderTarget?.title ?? ""}
          items={reorderTarget?.items ?? []}
          loading={isReordering}
          onSave={saveOrder}
        />
      </RenderWhen>
    </div>
  );
};
